import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';

const ComplianceMetricsPanel = ({ reviews }) => {
  // Aggregate compliance metrics from all reviews
  const aggregateMetrics = React.useMemo(() => {
    if (!reviews || reviews?.length === 0) return null;

    const metrics = reviews?.reduce((acc, review) => {
      const complianceData = review?.complianceSuccessMetrics || {};

      // Aggregate NASFUND accuracy
      if (complianceData?.nasfund_accuracy_improvement) {
        const value = parseFloat(complianceData?.nasfund_accuracy_improvement?.replace('%', ''));
        if (!isNaN(value)) {
          acc?.nasfundAccuracy?.push(value);
        }
      }

      // Aggregate SWT processing efficiency
      if (complianceData?.swt_processing_efficiency) {
        const value = parseFloat(complianceData?.swt_processing_efficiency?.replace('%', '')?.replace(' faster', ''));
        if (!isNaN(value)) {
          acc?.swtEfficiency?.push(value);
        }
      }

      // Aggregate Employment Act adherence
      if (complianceData?.employment_act_adherence_score) {
        const value = parseFloat(complianceData?.employment_act_adherence_score?.replace('%', ''));
        if (!isNaN(value)) {
          acc?.employmentActScore?.push(value);
        }
      }

      // Sum cost savings
      if (complianceData?.cost_savings_kina) {
        acc.totalCostSavings += parseFloat(complianceData?.cost_savings_kina) || 0;
      }

      // Sum violations prevented
      if (complianceData?.compliance_violations_prevented) {
        acc.totalViolationsPrevented += parseInt(complianceData?.compliance_violations_prevented) || 0;
      }

      return acc;
    }, {
      nasfundAccuracy: [],
      swtEfficiency: [],
      employmentActScore: [],
      totalCostSavings: 0,
      totalViolationsPrevented: 0
    });

    // Calculate averages
    const avgNasfund = metrics?.nasfundAccuracy?.length > 0
      ? metrics?.nasfundAccuracy?.reduce((sum, val) => sum + val, 0) / metrics?.nasfundAccuracy?.length
      : 0;

    const avgSwt = metrics?.swtEfficiency?.length > 0
      ? metrics?.swtEfficiency?.reduce((sum, val) => sum + val, 0) / metrics?.swtEfficiency?.length
      : 0;

    const avgEmploymentAct = metrics?.employmentActScore?.length > 0
      ? metrics?.employmentActScore?.reduce((sum, val) => sum + val, 0) / metrics?.employmentActScore?.length
      : 0;

    return {
      avgNasfundAccuracy: avgNasfund?.toFixed(1),
      avgSwtEfficiency: avgSwt?.toFixed(1),
      avgEmploymentActScore: avgEmploymentAct?.toFixed(1),
      totalCostSavingsKina: metrics?.totalCostSavings,
      totalViolationsPrevented: metrics?.totalViolationsPrevented
    };
  }, [reviews]);

  if (!aggregateMetrics) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
        <div className="flex items-center gap-2 mb-6">
          <Icon name="CheckCircle2" size={20} className="text-emerald-600" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Compliance Success Analytics</h3>
        </div>
        <p className="text-gray-500 dark:text-gray-400 text-center py-8">No compliance metrics available</p>
      </div>
    );
  }

  const metrics = [
    {
      label: 'NASFUND Accuracy Improvement',
      value: `${aggregateMetrics?.avgNasfundAccuracy}%`,
      icon: 'TrendingUp',
      color: 'text-blue-600'
    },
    {
      label: 'SWT Processing Efficiency',
      value: `${aggregateMetrics?.avgSwtEfficiency}%`,
      icon: 'Zap',
      color: 'text-purple-600'
    },
    {
      label: 'Employment Act Adherence',
      value: `${aggregateMetrics?.avgEmploymentActScore}%`,
      icon: 'Shield',
      color: 'text-emerald-600'
    },
    {
      label: 'Total Cost Savings',
      value: `K${aggregateMetrics?.totalCostSavingsKina?.toLocaleString()}`,
      icon: 'DollarSign',
      color: 'text-green-600'
    },
    {
      label: 'Violations Prevented',
      value: aggregateMetrics?.totalViolationsPrevented,
      icon: 'AlertTriangle',
      color: 'text-orange-600'
    }
  ];

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
      <div className="flex items-center gap-2 mb-6">
        <Icon name="CheckCircle2" size={20} className="text-emerald-600" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Compliance Success Analytics</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {metrics?.map((metric, index) => (
          <div key={index} className="p-4 rounded-lg bg-gray-50 dark:bg-slate-700/50 border border-gray-200 dark:border-slate-600">
            <div className="flex items-center gap-3 mb-2">
              <Icon name={metric?.icon} size={18} className={metric?.color} />
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400">{metric?.label}</p>
            </div>
            <p className="text-xl font-bold text-gray-900 dark:text-white">{metric?.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ComplianceMetricsPanel;